import { apiRequest } from '../../../api/client'

export interface PartnerWhitelistEntry {
  id: number
  ownerCompanyId: number
  partnerCompanyId: number
  partnerCompanyName: string
  delayMs: number
  enabled: boolean
}

export interface PartnerCompanyOption {
  id: number
  name: string
}

export interface PartnerQuoteView {
  amount: number
  currency: string
  sentAt: string | null
  current: boolean
  state: 'SENT' | 'PENDING_CONFIRMATION' | 'OVERRIDDEN'
  rank: number | null
}

export interface PartnerSharedOffer {
  shareId: number
  sourceOfferId: number
  // Numer transportu Transporeon (= to, co firma A widzi u siebie jako referencję). Tylko dla
  // transporeon, inaczej null. B podaje go A, by A odnalazła fracht (sam #shareId jest dla A nieznany).
  transporeonId: string | null
  shipperName: string
  loadPlace: string | null
  unloadPlace: string | null
  loadCountry: string | null
  unloadCountry: string | null
  unifiedMissingFields: string[] | null
  loadTime: string | null
  unloadTime: string | null
  loadStartsAt: string | null
  unloadStartsAt: string | null
  auctionDeadlineAt: string | null
  offerEditEndAt: string | null // Q1: do kiedy można zmienić ofertę (gate „Zmień")
  offerWithdrawalEndAt: string | null // Q1: do kiedy można wycofać ofertę (gate „Wycofaj")
  sourceStatus: string | null
  contactCompany: string | null
  contactPhone: string | null
  contactPerson: string | null
  contactEmail: string | null
  shareMode: 'AUTO' | 'MANUAL'
  sharedAt: string
  quotable: boolean
  referencePrice: number | null
  referenceCurrency: string | null
  myQuote: PartnerQuoteView | null
  insurancePerCaseEur: number | null
  insuranceTotalEur: number | null
  orderFeeEur: number | null
  offerCurrency: string | null
  // Q3: specyfikacja frachtu (parytet z zakładką Transporeon). Rodzaj towaru niedostępny w API.
  vehicleName: string | null
  weight: number | null
  weightUnit: string | null
  length: number | null
  lengthUnit: string | null
}

export interface PartnerShareInfo {
  shareId: number
  sourceOfferId: number
  partnerCompanyId: number
  partnerCompanyName: string
  shareMode: 'AUTO' | 'MANUAL'
  status: 'ACTIVE' | 'REVOKED' | 'CLOSED' | 'SUPPRESSED'
  sharedAt: string
  loadPlace: string | null
  unloadPlace: string | null
}

export interface AutoShareSourceFlag {
  sourceSystem: string
  enabled: boolean
}

/** Identyfikacja oferty: ops-id (lista B) albo para źródłowa (strony platformowe A). */
export type ShareOfferTarget =
  | { offerId: number }
  | { sourceSystem: string; sourceId: string }

export const fetchWhitelist = (token: string, signal?: AbortSignal) =>
  apiRequest<PartnerWhitelistEntry[]>('/api/partners/whitelist', {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const addPartner = (token: string, payload: { partnerCompanyId: number; delayMs: number }) =>
  apiRequest<{ id: number }>('/api/partners/whitelist', {
    method: 'POST',
    token,
    body: payload,
  })

export const updatePartner = (
  token: string,
  id: number,
  payload: { delayMs: number; enabled: boolean }
) =>
  apiRequest<{ ok: boolean }>(`/api/partners/whitelist/${id}`, {
    method: 'PATCH',
    token,
    body: payload,
  })

export const removePartner = (token: string, id: number) =>
  apiRequest<{ ok: boolean }>(`/api/partners/whitelist/${id}`, {
    method: 'DELETE',
    token,
  })

export const searchCompanies = (token: string, q: string, signal?: AbortSignal) =>
  apiRequest<PartnerCompanyOption[]>(`/api/partners/companies?q=${encodeURIComponent(q)}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

/** Dodatkowe warunki zlecenia firmy A (doklejane na dole PDF zlecenia dla B). */
export const fetchOrderTerms = (token: string, signal?: AbortSignal) =>
  apiRequest<{ orderTerms: string }>('/api/partners/order-terms', {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const saveOrderTerms = (token: string, orderTerms: string) =>
  apiRequest<{ ok: boolean }>('/api/partners/order-terms', {
    method: 'PUT',
    token,
    body: { orderTerms },
  })

export const shareOffer = (
  token: string,
  payload: ShareOfferTarget & { partnerCompanyId: number }
) =>
  apiRequest<{ ok: boolean }>('/api/partners/shares', {
    method: 'POST',
    token,
    body: payload,
  })

export const revokeShare = (token: string, shareId: number) =>
  apiRequest<{ ok: boolean }>(`/api/partners/shares/${shareId}`, {
    method: 'DELETE',
    token,
  })

export const fetchSharesForOffer = (token: string, target: ShareOfferTarget, signal?: AbortSignal) => {
  const params =
    'offerId' in target
      ? `offerId=${encodeURIComponent(String(target.offerId))}`
      : `sourceSystem=${encodeURIComponent(target.sourceSystem)}&sourceId=${encodeURIComponent(target.sourceId)}`
  return apiRequest<PartnerShareInfo[]>(`/api/partners/shares?${params}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })
}

export const fetchActiveShares = (token: string, offset = 0, limit = 50, signal?: AbortSignal) =>
  apiRequest<PartnerShareInfo[]>(`/api/partners/shares/active?offset=${offset}&limit=${limit}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const fetchIncomingOffers = (token: string, offset = 0, limit = 100, signal?: AbortSignal) =>
  apiRequest<PartnerSharedOffer[]>(`/api/partners/incoming-offers?offset=${offset}&limit=${limit}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const fetchIncomingOffersCount = (token: string, signal?: AbortSignal) =>
  apiRequest<{ count: number }>('/api/partners/incoming-offers/count', {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export interface PartnerPrefillResponse {
  draft?: Record<string, unknown>
  missingFields?: string[]
  aiEnrichmentRequired?: boolean
  filledFields?: Record<string, unknown>
}

export const fetchIncomingOfferDetail = (token: string, shareId: number, signal?: AbortSignal) =>
  apiRequest<PartnerSharedOffer>(`/api/partners/incoming-offers/${shareId}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const prefillSharedOffer = (token: string, shareId: number) =>
  apiRequest<PartnerPrefillResponse>(`/api/partners/incoming-offers/${shareId}/prefill`, {
    method: 'POST',
    token,
  })

export interface QuoteOutcome {
  ok: boolean
  myQuote: { amount: number; currency: string; sentAt: string }
}

export const quoteOffer = (
  token: string,
  shareId: number,
  amount: number,
  bindingUntil?: string | null, // Q2: ważność oferty (ISO-8601); pomijane gdy puste = bez ważności
) =>
  apiRequest<QuoteOutcome>(`/api/partners/incoming-offers/${shareId}/quote`, {
    method: 'POST',
    token,
    body: { amount, ...(bindingUntil ? { bindingUntil } : {}) },
  })

export interface PartnerOrder {
  id: number
  perspective: 'OWNER' | 'PARTNER'
  orderNumber: string | null
  status: 'NEW' | 'CANCELLED' | 'CHANGED'
  acceptedAt: string
  statusChangedAt: string | null
  shipperName: string | null
  loadCompanyName: string | null
  loadCity: string | null
  loadZip: string | null
  loadCountry: string | null
  loadFromAt: string | null
  loadUntilAt: string | null
  unloadCompanyName: string | null
  unloadCity: string | null
  unloadZip: string | null
  unloadCountry: string | null
  unloadFromAt: string | null
  unloadUntilAt: string | null
  vehicleName: string | null
  weight: number | null
  weightUnit: string | null
  amount: number
  currency: string
  contactPerson: string | null
  contactEmail: string | null
  contactPhone: string | null
  // wyłącznie perspektywa OWNER:
  partnerCompanyName?: string
  quotedByPartner?: boolean
  totalAmount?: number
  marginAmount?: number
  transportId?: string
}

export interface PartnerMyQuote {
  id: number
  shareId: number
  status: 'SENT' | 'PENDING_CONFIRMATION' | 'SUPERSEDED' | 'ACCEPTED' | 'LOST' | 'WITHDRAWN' | 'OVERRIDDEN'
  amount: number
  currency: string
  sentAt: string
  rank: number | null
  shipperName: string | null
  loadPlace: string | null
  unloadPlace: string | null
  orderNumber: string | null
}

export const fetchMyQuotes = (token: string, offset = 0, limit = 50, signal?: AbortSignal) =>
  apiRequest<PartnerMyQuote[]>(`/api/partners/quotes?offset=${offset}&limit=${limit}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export interface SettlementOwnerItem {
  orderNumber: string | null
  partnerName: string
  acceptedAt: string
  status: 'NEW' | 'CHANGED'
  amount: number
  marginAmount: number
  totalAmount: number
  currency: string
}

export interface SettlementOwnerTotals {
  partnerName: string
  currency: string
  orderCount: number
  amountSum: number
  marginSum: number
  totalSum: number
}

export interface SettlementPartnerItem {
  orderNumber: string | null
  ownerName: string
  acceptedAt: string
  status: 'NEW' | 'CHANGED'
  amount: number
  currency: string
  feeEur: number | null
}

export interface SettlementResponse {
  month: string
  asOwner: SettlementOwnerItem[]
  asOwnerTotals: SettlementOwnerTotals[]
  asPartner: SettlementPartnerItem[]
  asPartnerFeeTotalEur: number
}

export const fetchSettlements = (token: string, month: string, signal?: AbortSignal) =>
  apiRequest<SettlementResponse>(`/api/partners/settlements?month=${encodeURIComponent(month)}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const settlementsExportPath = (month: string, perspective: 'owner' | 'partner') =>
  `/api/partners/settlements/export?month=${encodeURIComponent(month)}&perspective=${perspective}`

export const withdrawQuote = (token: string, shareId: number) =>
  apiRequest<{ ok: boolean }>(`/api/partners/incoming-offers/${shareId}/quote`, {
    method: 'DELETE',
    token,
  })

export const fetchOrders = (token: string, offset = 0, limit = 50, signal?: AbortSignal) =>
  apiRequest<PartnerOrder[]>(`/api/partners/orders?offset=${offset}&limit=${limit}`, {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const fetchOrdersCount = (token: string, signal?: AbortSignal) =>
  apiRequest<{ new: number; cancelled: number }>('/api/partners/orders/count', {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const orderDocumentPath = (orderId: number) => `/api/partners/orders/${orderId}/document`

export const fetchAutoSources = (token: string, signal?: AbortSignal) =>
  apiRequest<AutoShareSourceFlag[]>('/api/partners/auto-sources', {
    token,
    skipGlobalLoading: true,
    ...(signal ? { signal } : {}),
  })

export const setAutoSources = (token: string, flags: Record<string, boolean>) =>
  apiRequest<{ ok: boolean }>('/api/partners/auto-sources', {
    method: 'PUT',
    token,
    body: { flags },
  })
